import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatMenu } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { _error, _log, _warn } from '@shared/aux_helper_environment';
import { _cloneDeep, _ms } from '@shared/aux_helper_functions';
import { AiDashboardEventsService } from 'app/ai-dashboard/services/ai-dashboard-events.service';
import { AiDashboardStorageService } from 'app/ai-dashboard/services/ai-dashboard-storage.service';
import { GenieColumnType, GenieStatementResponse } from 'app/services/agents/databricks-genie-ai-chat.models';
import { DataBricksAiGenieChatResponse, DataBricksAiGenieChatService } from 'app/services/agents/databricks-genie-ai-chat.service';
import { DevSettingsService } from 'core/services/dev-settings.service';
import { IanTranslateService, PrismaDynamicEnv } from 'core/services/ian-core-singleton.service';
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import {
  _formatColumnTitle,
  DataBricksAiGenie_AnalyzeMultiSeriesChartData,
  DataBricksAiGenie_AnalyzeSimpleChartData,
  getNumericColumns,
  isDateString,
} from './_aux_dataBricksAiGenieQueryProcessTable';
import { ColumnSelectorDialogComponent } from './column-selector-dialog.component';
import { DataBricksAiGenieQueryProcessChartComponent } from './databricks-genie-ai-query-process-chart.component';
import { PinCardDialogComponent, PinCardDialogResult } from './pin-card-dialog.component';

//Test: Quero un listado de dos columnas, una con el nombre de la tienda y la otra con el valor de la venta acumulada en $

export type DataBricksAiGenie_ChartAnalysisResult_ChartType = 'pie' | 'bar' | 'horizontalBar' | 'time_line';

export interface DataBricksAiGenie_ChartAnalysisResult {
  isVisualizationAvailable: boolean;
  visualization?: {
    series: Array<{ label: string; value: number }>;
    numericColumnName: string;
    labelColumnName: string;
    chartType: DataBricksAiGenie_ChartAnalysisResult_ChartType;
    // Multi-serie (cuando el usuario elige >1 columna numérica para comparar)
    multiSeries?: Array<{ name: string; data: Array<{ label: string; value: number }> }>;
    numericColumnNames?: string[];
    useDualAxis?: boolean;
  };
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

@Component({
  selector: 'query-process-result',
  template: `
    <div *ngIf="isDialogMode" class="dialog-header">
      <h1 mat-dialog-title fxFlex>{{ getTableTitle() }}</h1>
      <button mat-icon-button (click)="closeDialog()" style="top: -16px; position: relative;">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- --  -->

    <div *ngIf="initiedTable" class="table-container" [ngClass]="{ 'is-dialog-mode': isDialogMode }">
      <cdk-virtual-scroll-viewport
        [tvsItemSize]="28"
        [headerHeight]="32"
        bufferMultiplier="2"
        class="viewport"
        [ngStyle]="{ height: getViewportHeight(), 'min-height': isDialogMode ? 'none' : getViewportHeight() }"
      >
        <div *ngIf="shouldShowSearchInput()" class="search-container">
          <mat-form-field appearance="outline" class="search-field">
            <mat-icon matPrefix>search</mat-icon>
            <input matInput [(ngModel)]="searchValue" (keyup)="applyFilter($event)" [placeholder]="getSearchPlaceholder()" />
            <button mat-icon-button matSuffix *ngIf="searchValue" (click)="clearSearch()">
              <mat-icon>close</mat-icon>
            </button>
          </mat-form-field>
        </div>

        <!-- Warning banner when results are truncated -->
        <div *ngIf="shouldShowFullDownloadButton()" class="results-limit-warning">
          <mat-icon>info</mat-icon>
          <span>
            {{ getResultsLimitWarningMessage() }}
            <a href="javascript:void(0)" (click)="downloadFullCSV()" class="download-link" [class.is-loading]="isDownloadingFullCSV">
              {{ getClickHereText() }}
            </a>
          </span>
        </div>

        <table mat-table [dataSource]="dataSource" class="results-table">
          <ng-container *ngFor="let column of displayedColumns" [matColumnDef]="column">
            <th mat-header-cell *matHeaderCellDef (click)="sortData(column)" class="sortable-header">
              {{ getColumnHeader(column) }}
              <mat-icon *ngIf="sortColumn === column" class="sort-icon">
                {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
              </mat-icon>
            </th>
            <td mat-cell *matCellDef="let element">{{ element[column] }}</td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
        <div *ngIf="!dataSource?.filteredData?.length" class="no-data-message">{{ getNoDataMessage() }}</div>
      </cdk-virtual-scroll-viewport>

      <div *ngIf="shouldShowFooter()" class="table-footer">
        <span class="record-count" [class.limit-exceeded]="shouldShowFullDownloadButton()">
          {{ dataSource?.filteredData?.length || 0 }} {{ getRecordsText() }}
        </span>
        <div class="table-footer-buttons">
          <button
            *ngIf="shouldShowFullDownloadButton()"
            mat-icon-button
            (click)="downloadFullCSV()"
            [matTooltip]="getDownloadFullCsvTooltip()"
            color="accent"
            [disabled]="isDownloadingFullCSV"
          >
            <mat-icon *ngIf="!isDownloadingFullCSV">cloud_download</mat-icon>
            <mat-progress-spinner
              *ngIf="isDownloadingFullCSV"
              mode="indeterminate"
              diameter="20"
              style="margin: 6px;"
            ></mat-progress-spinner>
          </button>
          <button *ngIf="!shouldShowFullDownloadButton()" mat-icon-button (click)="exportToCSV()" [matTooltip]="getExportCsvTooltip()">
            <mat-icon>cloud_download</mat-icon>
          </button>
          <button
            *ngIf="shouldDisplayPinButton()"
            mat-icon-button
            [disabled]="isPinLimitExceeded()"
            (click)="openPinDialog()"
            [matTooltip]="getPinTooltip()"
          >
            <mat-icon [style.color]="isPinLimitExceeded() ? '#ccc' : ''">push_pin</mat-icon>
          </button>
          <button
            *ngIf="hasQuery() && canUseDevMode()"
            mat-icon-button
            [matMenuTriggerFor]="queryPopover"
            [matTooltip]="getViewQueryTooltip()"
            #queryBtn="matTooltip"
          >
            <mat-icon>code</mat-icon>
          </button>
          <button
            [disabled]="!shouldShowFooterVisualization()"
            mat-icon-button
            (click)="openChartView()"
            [matTooltip]="getChartButtonTooltip()"
          >
            <mat-icon>{{ getChartIcon(chartMetadata?.visualization?.chartType || 'bar') }}</mat-icon>
          </button>
          <button mat-icon-button (click)="openDialog()" [matTooltip]="getExpandTableTooltip()" *ngIf="!isDialogMode">
            <mat-icon>fullscreen</mat-icon>
          </button>
        </div>
      </div>
      <mat-menu #queryPopover="matMenu" class="query-popover">
        <div class="query-container">
          <pre class="query-content">{{ getQuery() }}</pre>
          <button mat-icon-button class="copy-button" (click)="copyQuery()" [matTooltip]="getCopyQueryTooltip()">
            <mat-icon>content_copy</mat-icon>
          </button>
        </div>
      </mat-menu>
    </div>
  `,
  styles: [
    `
      .search-container {
        padding: 8px;
        background: #fafafa;
      }

      .search-field {
        width: 100%;
        font-size: 14px;
      }

      :host ::ng-deep .search-field .mat-form-field-wrapper {
        padding-bottom: 0;
      }

      :host ::ng-deep .search-field .mat-form-field-infix {
        padding: 6px 0;
        top: -3px;
        left: 4px;
      }

      :host ::ng-deep .search-field .mat-form-field-outline {
        background: white;
      }

      .results-limit-warning {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        margin: 0 8px 8px 8px;
        background: #fff3cd;
        border: 1px solid #ffc107;
        border-radius: 4px;
        color: #856404;
        font-size: 13px;
      }

      .results-limit-warning mat-icon {
        color: #ffc107;
      }

      .download-link {
        color: #856404;
        font-weight: 600;
        text-decoration: underline;
        cursor: pointer;
        margin-left: 4px;
      }

      .download-link:hover {
        color: #533f03;
      }

      .download-link.is-loading {
        opacity: 0.5;
        pointer-events: none;
        text-decoration: none;
      }

      .table-container {
        position: relative;
        border-radius: 4px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        box-shadow: 1px 1px 1px 0px rgba(0, 0, 0, 0.1);
        overflow: visible;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
      }

      .table-container.is-dialog-mode {
        height: calc(95vh - 120px);
        max-height: calc(95vh - 120px);
      }

      .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0;
        background: #fafafa;
      }

      .viewport {
        width: 100%;
        max-width: 100%;
        background: #fafafa;
        overflow: auto;
        box-sizing: border-box;
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        flex: 1;
      }

      .table-container.is-dialog-mode .viewport {
        overflow: auto;
        min-height: 300px;
      }

      .results-table {
        width: 100%;
        table-layout: auto;
        box-sizing: border-box;
      }

      th.mat-header-cell,
      td.mat-cell {
        min-width: 80px;
        max-width: 200px;
        padding: 4px 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: rgba(0, 0, 0, 0.7);
        box-sizing: border-box;
      }

      th.mat-header-cell {
        background: #fafafa;
      }

      .table-container.is-dialog-mode th.mat-header-cell,
      .table-container.is-dialog-mode td.mat-cell {
        max-width: 640px;
      }

      :host ::ng-deep tr.mat-row {
        height: 32px !important;
      }

      :host ::ng-deep tr.mat-header-row {
        height: 36px !important;
        top: 0;
        z-index: 1;
        background: #fafafa;
      }

      :host ::ng-deep .cdk-virtual-scroll-content-wrapper {
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        overflow-x: auto !important;
      }

      // Estilizar scrollbars
      .viewport::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }

      .viewport::-webkit-scrollbar-track {
        background: #f1f1f1;
      }

      .viewport::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.12);
        border-radius: 16px;
      }

      .table-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 12px;
        border-top: 1px solid rgba(0, 0, 0, 0.12);
        background: #f8f8f8;
        min-height: 40px;
        box-sizing: border-box;
        flex-shrink: 0;
      }

      .table-container.is-dialog-mode .table-footer {
        background: #fafafa;
      }

      .record-count {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.54);
        margin-top: 2px;
        transition: color 0.3s ease;
      }

      .record-count.limit-exceeded {
        color: #f44336; /* Material Red 500 */
        font-weight: 600;
        background: rgba(244, 67, 54, 0.05);
        padding: 2px 6px;
        border-radius: 4px;
      }

      .mat-icon-button {
        width: 32px;
        height: 32px;
        line-height: 32px;
      }

      .no-data-message {
        text-align: center;
        padding: 16px;
        color: rgba(0, 0, 0, 0.54);
        background: white;
      }

      .table-footer-buttons {
        display: flex;
        gap: 4px;
      }

      .sortable-header {
        cursor: pointer;
        user-select: none;
      }

      .sortable-header:hover {
        background: rgb(235, 235, 235) !important;
      }

      .sort-icon {
        font-size: 16px;
        position: absolute;
        right: 8px;
        top: 60%;
        transform: translateY(-50%);
        opacity: 0.7;
        width: 16px;
        height: 16px;
      }

      .query-popover {
        max-width: none !important;
      }

      .query-container {
        position: relative;
        padding: 8px;
      }

      .query-content {
        max-width: 500px;
        padding: 8px;
        margin: 0;
        white-space: pre-wrap;
        font-family: monospace;
        background: #f5f5f5;
        border-radius: 4px;
        font-size: 12px;
        line-height: 1.3;
        overflow-x: hidden;
        padding-right: 36px; /* Espacio para el botón */
      }

      .copy-button {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 24px;
        height: 24px;
        line-height: 24px;
        opacity: 0.7;
      }

      .copy-button:hover {
        opacity: 1;
      }

      .copy-button .mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        line-height: 16px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataBricksAiGenieQueryProcessTableComponent implements OnInit {
  @ViewChild('queryPopover') queryPopover: MatMenu;

  @Input() queryProcessResult: GenieStatementResponse;
  @Input() readonly message: DataBricksAiGenieChatResponse;
  @Input() showPinButton = true; // Control para mostrar/ocultar el botón de pin

  isDialogMode = false;
  displayedColumns: string[] = [];
  dataSource: TableVirtualScrollDataSource<any>;
  initiedTable = false;
  originalData: any[] = [];
  searchValue = '';
  isDownloadingFullCSV = false;

  chartMetadata: DataBricksAiGenie_ChartAnalysisResult = {
    isVisualizationAvailable: false,
  };

  queryProcessResultNumeric = null;

  sortColumn: string = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  private config = this.prismaDynamicEnv.getConf('brandCustomization.dataBricksGenieConfig.TABLE') || {};

  constructor(
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private cdRef: ChangeDetectorRef,
    @Optional() private dialogRef: MatDialogRef<DataBricksAiGenieQueryProcessTableComponent>,
    private prismaDynamicEnv: PrismaDynamicEnv,
    @Optional() private aiDashboardStorageService: AiDashboardStorageService,
    private aiDashboardEventsService: AiDashboardEventsService,
    private router: Router,
    private translate: IanTranslateService,
    private devSettings: DevSettingsService,
    private snackBar: MatSnackBar,
    private dataBricksAiGenieChatService: DataBricksAiGenieChatService
  ) {
    this.isDialogMode = data?.isDialogMode === true;
    if (this.isDialogMode) this.queryProcessResult = data.queryProcessResult;
  }

  ngOnInit() {
    console.log('Table ngOnInit - isDialogMode:', this.isDialogMode, 'queryProcessResult:', this.queryProcessResult);
    this.initializeTable();
  }

  private initializeTable() {
    console.log('initializeTable called - queryProcessResult:', this.queryProcessResult);
    if (!this.queryProcessResult?.statement_response) {
      console.log('No statement_response found, returning early');
      return;
    }

    this.resetAllCaches();

    const { manifest, result } = this.queryProcessResult.statement_response;
    this.queryProcessResultNumeric = getNumericColumns(this.queryProcessResult);

    // Primero transformamos los datos
    const transformedData = result.data_typed_array.map(row => {
      const transformedRow = {};
      row.values.forEach((value, index) => {
        const columnName = manifest.schema.columns[index].name;
        const columnType = manifest.schema.columns[index];
        const rawValue = value.str || value.num || value.bool;
        if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
          transformedRow[columnName] = this.formatCellValue(rawValue, columnType);
        }
      });
      return transformedRow;
    });

    // Identificamos qué columnas tienen al menos un valor
    const columnsWithValues = new Set<string>();
    transformedData.forEach(row => {
      Object.keys(row).forEach(key => {
        columnsWithValues.add(key);
      });
    });

    // Filtramos las columnas que tienen valores
    this.displayedColumns = manifest.schema.columns.map(col => col.name).filter(colName => columnsWithValues.has(colName));

    // Store original data
    this.originalData = transformedData;

    // Initialize TableVirtualScrollDataSource
    this.dataSource = new TableVirtualScrollDataSource(transformedData);

    console.log('Table initialized successfully, setting initiedTable = true');
    this.initiedTable = true;

    // Force change detection, especially important for dialog mode
    this.cdRef.detectChanges();

    this.analyzeDataVisualizationDealyed();
  }

  analyzeDataVisualizationDealyed(time = 16) {
    setTimeout(() => {
      this.analyzeDataVisualization(this.queryProcessResult);
    }, time);
  }

  get localStringFormat(): string {
    return navigator.language || navigator.languages[0] || 'default';
  }

  private formatColumnTitle(columnName: string): string {
    return _formatColumnTitle(columnName);
  }

  getColumnHeader(column: string): string {
    const schema = this.queryProcessResult?.statement_response?.manifest?.schema;
    if (!schema) return null;

    const columnDef = schema.columns.find(col => col.name === column);

    return this.formatColumnTitle(columnDef.name);
  }

  private formatDate(value: string): string {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return value;
    }
  }

  private formatCellValue(value: any, columnType: GenieColumnType): string | number {
    // Si el valor es null o undefined, retornamos el valor sin procesar
    if (value === null || value === undefined) return value;

    // Manejar tipos según el schema
    switch (columnType.type_name.toLowerCase()) {
      case 'timestamp':
      case 'string':
      case 'varchar':
      case 'char':
      case 'text':
        if (isDateString(value)) return this.formatDate(value);
        return value;

      case 'boolean':
        if (this.config.transformBooleans) {
          return value === true || value === 'true' ? 'Si' : 'No';
        }
        return value;

      case 'int':

      case 'integer':
      case 'bigint':
      case 'smallint':
        return Number(value).toLocaleString(this.localStringFormat);

      case 'decimal':
      case 'numeric':
      case 'float':
      case 'double':
        const num = Number(value);
        const hasDecimals = num % 1 !== 0;

        // Solo redondear si es mayor a 10000 Y tiene decimales
        if (Math.abs(num) > 10000 && hasDecimals) {
          return Math.round(num).toLocaleString(this.localStringFormat);
        }

        return num.toLocaleString(this.localStringFormat);

      default:
        return value;
    }
  }

  exportToCSV(): void {
    if (!this.dataSource?.filteredData?.length) return;

    // Crear línea de encabezados
    const headers = this.displayedColumns.map(col => this.getColumnHeader(col));

    // Crear líneas de datos
    const rows = this.dataSource.filteredData.map(row =>
      this.displayedColumns.map(col => {
        const value = row[col];
        // Escapar comas y comillas si es necesario
        return typeof value === 'string' && (value.includes(',') || value.includes('"')) ? `"${value.replace(/"/g, '""')}"` : value;
      })
    );

    // Combinar todo en un string CSV
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');

    // Crear y descargar el archivo
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `datos_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Descarga el CSV completo ejecutando el query y convirtiendo los datos a CSV
   */
  downloadFullCSV(): void {
    if (!this.hasQuery()) {
      _warn('[downloadFullCSV] No query available');
      return;
    }

    const query = this.getQuery();
    const genieConfig = this.dataBricksAiGenieChatService.getGenieConfig();

    _log('[downloadFullCSV] Iniciando descarga completa...', {
      query: query.substring(0, 100),
      catalog: genieConfig.catalog,
      schema: genieConfig.schema,
      useExternalLinks: genieConfig.useExternalLinksForCSV,
    });

    // Mostrar loading
    const snackBarRef = this.snackBar.open(
      this.translate.instant('DATA_VISUALIZATION.TABLE.PREPARING_DOWNLOAD') || 'Preparando descarga...',
      null,
      { duration: 0 } // Indefinido hasta que termine
    );

    this.isDownloadingFullCSV = true;
    this.cdRef.detectChanges();

    const downloadMethod = genieConfig.useExternalLinksForCSV ? 'executeStatementDirectExternalCSV' : 'executeStatementDirectCSVDownload';

    this.dataBricksAiGenieChatService[downloadMethod](query, genieConfig.warehouseId, genieConfig.catalog, genieConfig.schema).subscribe({
      next: async response => {
        // CASO 1: Descarga por EXTERNAL_LINKS (Presigned URLs de Databricks en formato CSV)
        if (genieConfig.useExternalLinksForCSV && response.result?.external_links?.length) {
          _log('[downloadFullCSV] Recibidos links externos, iniciando descarga...', {
            linksCount: response.result.external_links.length,
          });

          const totalLinks = response.result.external_links.length;

          // Descargar cada link (Databricks puede paginar el resultado en múltiples links)
          for (let i = 0; i < totalLinks; i++) {
            const linkObj = response.result.external_links[i];
            const fileName = `datos_completos_${new Date().toISOString().split('T')[0]}_part${i + 1}.csv`;

            const downloadMsg = (
              this.translate.instant('DATA_VISUALIZATION.TABLE.DOWNLOADING_PART') || 'Descargando parte {current} de {total}...'
            )
              .replace('{current}', (i + 1).toString())
              .replace('{total}', totalLinks.toString());

            this.snackBar.open(downloadMsg, null, { duration: 2000 });

            try {
              await this.downloadExternalLink(linkObj.external_link, fileName);
            } catch (err) {
              _error('[downloadFullCSV] Error en parte ' + (i + 1), err);
            }
          }

          this.isDownloadingFullCSV = false;
          this.cdRef.detectChanges();
          snackBarRef.dismiss();

          // Notificar éxito
          this.snackBar.open(this.translate.instant('DATA_VISUALIZATION.TABLE.DOWNLOAD_READY') || 'Descarga completada', null, {
            duration: 3000,
          });
          return;
        }

        this.isDownloadingFullCSV = false;
        this.cdRef.detectChanges();
        snackBarRef.dismiss();

        // CASO 2: Descarga INLINE (formato JSON_ARRAY convencional)
        _log('[downloadFullCSV] Datos recibidos inline, generando CSV...', {
          rowCount: response.result?.data_array?.length,
          columns: response.manifest?.schema?.columns?.length,
        });

        // Convertir los datos a CSV
        const csvContent = this.convertDatabricksResponseToCSV(response);

        if (!csvContent) {
          _error('[downloadFullCSV] No se pudo generar el CSV');
          this.snackBar.open(this.translate.instant('DATA_VISUALIZATION.TABLE.DOWNLOAD_ERROR') || 'Error al generar CSV', null, {
            duration: 5000,
            panelClass: ['error-snackbar'],
          });
          return;
        }

        // Crear blob y descargar
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `datos_completos_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Notificar éxito
        this.snackBar.open(this.translate.instant('DATA_VISUALIZATION.TABLE.DOWNLOAD_READY') || 'Descarga completada', null, {
          duration: 3000,
        });
      },
      error: error => {
        this.isDownloadingFullCSV = false;
        this.cdRef.detectChanges();
        snackBarRef.dismiss();

        _error('[downloadFullCSV] Error descargando datos:', error);

        this.snackBar.open(this.translate.instant('DATA_VISUALIZATION.TABLE.DOWNLOAD_ERROR') || 'Error al descargar CSV completo', null, {
          duration: 5000,
          panelClass: ['error-snackbar'],
        });
      },
    });
  }

  /**
   * Descarga un archivo desde una URL externa (usado para EXTERNAL_LINKS)
   * Usa fetch para poder forzar el nombre del archivo y la extensión
   */
  private async downloadExternalLink(url: string, fileName: string): Promise<void> {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();

      // Limpieza
      setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      _warn('[downloadExternalLink] Error:', error);
      // Fallback a descarga directa (el nombre será el del servidor pero al menos descarga algo)
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.click();
    }
  }

  /**
   * Convierte una respuesta de Databricks Statement API a formato CSV
   */
  private convertDatabricksResponseToCSV(response: any): string {
    if (!response?.manifest?.schema?.columns || !response?.result?.data_array) {
      _error('[convertDatabricksResponseToCSV] Invalid response structure');
      return null;
    }

    const columns = response.manifest.schema.columns;
    const data = response.result.data_array;

    // Crear línea de encabezados
    const headers = columns.map(col => col.name);

    // Crear líneas de datos
    const rows = data.map(row => {
      return row.map(value => {
        // Escapar comas y comillas si es necesario
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        return stringValue.includes(',') || stringValue.includes('"') ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
      });
    });

    // Combinar todo en un string CSV
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');

    return csv;
  }

  openDialog(): void {
    const config = new MatDialogConfig();

    config.width = '90%';
    config.height = '90%';
    config.data = { queryProcessResult: this.queryProcessResult, isDialogMode: true };
    config.autoFocus = false;

    this.dialog.open(DataBricksAiGenieQueryProcessTableComponent, config);
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private filterQueryResultByDisplayedColumns(queryResult: GenieStatementResponse): GenieStatementResponse {
    if (!queryResult?.statement_response) return queryResult;

    let queryResultLength = queryResult?.statement_response?.manifest?.schema?.columns?.length;
    let displayedColumnsLength = this.displayedColumns?.length;

    //Si no hay columnas filtradas usa el original por precaución
    if (queryResultLength === displayedColumnsLength) return queryResult;

    const original = _cloneDeep(queryResult);
    const { manifest, result } = original.statement_response;

    // Get indices of displayed columns
    const displayedIndices = this.displayedColumns
      .map(colName => manifest.schema.columns.findIndex(col => col.name === colName))
      .filter(idx => idx !== -1);

    // Filter schema columns
    const filteredColumns = displayedIndices.map(idx => manifest.schema.columns[idx]);

    // Filter data rows
    const filteredData = result.data_typed_array.map(row => ({
      values: displayedIndices.map(idx => row.values[idx]),
    }));

    return {
      ...queryResult,
      statement_response: {
        ...original.statement_response,
        manifest: {
          ...manifest,
          schema: {
            ...manifest.schema,
            columns: filteredColumns,
            column_count: filteredColumns.length,
          },
        },
        result: {
          ...result,
          data_typed_array: filteredData,
        },
      },
    };
  }

  analyzeDataVisualization($queryProcessResult) {
    if (!this.shouldShowFooterVisualization()) return;

    // Filter queryProcessResult by displayed columns before analysis
    const filteredQueryResult = this.filterQueryResultByDisplayedColumns($queryProcessResult);
    const chartAnalysis = DataBricksAiGenie_AnalyzeSimpleChartData(filteredQueryResult);

    if (chartAnalysis?.error) {
      _warn('[ChartAnalysis Error]', chartAnalysis.error, $queryProcessResult);
    }

    if (chartAnalysis?.isVisualizationAvailable) {
      this.chartMetadata = chartAnalysis;
      this.cdRef.detectChanges();
      return;
    }

    this.chartMetadata = {
      isVisualizationAvailable: false,
      error: chartAnalysis?.error,
    };
  }

  openChartView(): void {
    const numericCols: string[] = this.queryProcessResultNumeric || [];
    const totalCols = this.displayedColumns?.length || 0;
    const nonNumericCols = totalCols - numericCols.length;

    // Caso trivial: 1 label + 1 numeric — no hay nada que elegir, abrir directo.
    // Para cualquier otro caso abrimos el selector con sugerencias precargadas para que
    // el usuario pueda elegir etiqueta y comparar 1 o N series numéricas.
    const isTrivialAutoChart = numericCols.length === 1 && nonNumericCols === 1;

    if (!isTrivialAutoChart) {
      this.openColumnSelector();
      return;
    }

    this.openChartDialog();
  }

  private openChartDialog(): void {
    if (this.chartMetadata.visualization == null) {
      _error('No se pudo abrir el gráfico, no hay datos de visualización:', {
        chartMetadata: this.chartMetadata,
        displayedColumns: this.displayedColumns,
      });
      return;
    }

    const numericCols: string[] = (this.queryProcessResultNumeric as unknown as string[]) || [];
    // Para la etiqueta (eje X) permitimos elegir cualquier columna visible — el usuario
    // puede querer usar una numérica como eje (ej.: scatter conceptual). El analyzer maneja
    // la deduplicación si label coincide con una serie.
    const labelCandidates = [...(this.displayedColumns || [])];

    const config = new MatDialogConfig();
    config.width = '90%';
    config.height = '90%';
    config.data = {
      chartData: _cloneDeep(this.chartMetadata.visualization),
      isDialogMode: true,
      // Datos para los controles in-chart (tipo / etiqueta / series / eje):
      queryProcessResult: this.queryProcessResult,
      availableNumericColumns: numericCols,
      availableLabelColumns: labelCandidates,
    };
    config.autoFocus = false;

    const dialogRef = this.dialog.open(DataBricksAiGenieQueryProcessChartComponent, config);

    _log(['openChartView'], this.chartMetadata.visualization);

    // Si el chartMetadata fue generado por selección manual de columnas, resetearlo al cerrar
    if (this.displayedColumns.length > 2) {
      dialogRef.afterClosed().subscribe(() => {
        this.chartMetadata = {
          isVisualizationAvailable: false,
        };
        this.cdRef.detectChanges();
      });
    }
  }

  private openColumnSelector(): void {
    const numericColumns: string[] = (this.queryProcessResultNumeric as unknown as string[]) || [];

    // Sugerencias: si ya tenemos visualización detectada usamos sus columnas como default;
    // si hay >=2 numéricas, sugerimos también la segunda para activar el comparativo por default.
    const detected = this.chartMetadata?.visualization;
    const suggestedLabelColumn = detected?.labelColumnName || null;
    let suggestedNumericColumns: string[] | null = null;
    if (detected?.numericColumnNames?.length) {
      suggestedNumericColumns = detected.numericColumnNames;
    } else if (detected?.numericColumnName) {
      const base: string[] = [detected.numericColumnName];
      if (numericColumns.length >= 2) {
        const second = numericColumns.find((c: string) => c !== detected.numericColumnName);
        if (second) base.push(second);
      }
      suggestedNumericColumns = base;
    } else if (numericColumns.length >= 1) {
      suggestedNumericColumns = numericColumns.slice(0, 2);
    }

    const dialogRef = this.dialog.open(ColumnSelectorDialogComponent, {
      width: '400px',
      data: {
        columns: this.displayedColumns,
        numericColumns,
        filteredData: this.dataSource?.filteredData,
        suggestedLabelColumn,
        suggestedNumericColumns,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;

      // result.numericColumns es siempre string[] (1+ elementos)
      const selectedNumericCols: string[] = result.numericColumns || [];
      const labelColumn: string = result.labelColumn;

      if (selectedNumericCols.length === 0 || !labelColumn) {
        console.error('[openColumnSelector] selección inválida:', result);
        return;
      }

      let chartAnalysis;
      if (selectedNumericCols.length === 1) {
        // Path single-serie: igual que antes
        const filteredResult = this.createFilteredResult(selectedNumericCols, labelColumn);
        chartAnalysis = DataBricksAiGenie_AnalyzeSimpleChartData(filteredResult);
      } else {
        // Path multi-serie: usar el analyzer dedicado (preserva orden de columnas seleccionadas)
        const filteredResult = this.createFilteredResult(selectedNumericCols, labelColumn);
        chartAnalysis = DataBricksAiGenie_AnalyzeMultiSeriesChartData(filteredResult, selectedNumericCols, labelColumn);
      }

      if (chartAnalysis?.isVisualizationAvailable) {
        this.chartMetadata = chartAnalysis;
        // Abrir el chart directamente — NO llamar openChartView() que reabriría el selector.
        this.openChartDialog();
      } else {
        console.error('[openChartView] fail:', { result, chartAnalysis });
      }
    });
  }

  private createFilteredResult(numericColumns: string[], labelColumn: string): GenieStatementResponse {
    const original = _cloneDeep(this.queryProcessResult.statement_response);

    const labelIdx = original.manifest.schema.columns.findIndex(col => col.name === labelColumn);
    const numIdxs = numericColumns.map(name => original.manifest.schema.columns.findIndex(col => col.name === name));

    // Orden: [label, ...numerics] — los analyzers manejan ambos casos por nombre, así que el orden no es crítico,
    // pero conviene mantenerlo predecible.
    const selectedIdxs = [labelIdx, ...numIdxs];
    const selectedColumns = selectedIdxs.map(i => original.manifest.schema.columns[i]);

    const newSchema = {
      ...original.manifest.schema,
      column_count: selectedColumns.length,
      columns: selectedColumns,
    };

    const newData = original.result.data_typed_array.map(row => ({
      values: selectedIdxs.map(i => row.values[i]),
    }));

    return {
      ...this.queryProcessResult,
      statement_response: {
        ...original,
        manifest: {
          ...original.manifest,
          schema: newSchema,
        },
        result: {
          ...original.result,
          data_typed_array: newData,
        },
      },
    };
  }

  private getChartButtonTooltip(): string {
    if (!this.shouldShowFooterVisualization()) {
      return (
        this.translate.instant('DATA_VISUALIZATION.CHART.NO_VISUALIZATION') ||
        'Con los datos disponibles actualmente, no es posible generar el gráfico'
      );
    }

    if (!this.chartMetadata.isVisualizationAvailable) {
      return this.displayedColumns.length > 2
        ? this.translate.instant('DATA_VISUALIZATION.CHART.SELECT_COLUMNS') || 'Ver Gráfico, seleccionar columnas para visualizar'
        : this.translate.instant('DATA_VISUALIZATION.CHART.NO_VISUALIZATION') ||
            'Con los datos disponibles actualmente, no es posible generar el gráfico';
    }

    const viewChart = this.translate.instant('DATA_VISUALIZATION.CHART.VIEW_CHART') || 'Ver gráfico';
    return viewChart + ' ' + this.getChartTypeLabel(this.chartMetadata.visualization.chartType);
  }

  private getChartTypeLabel(type: DataBricksAiGenie_ChartAnalysisResult_ChartType): string {
    switch (type) {
      case 'pie':
        return this.translate.instant('DATA_VISUALIZATION.CHART.PIE_CHART') || 'de torta';
      case 'time_line':
        return this.translate.instant('DATA_VISUALIZATION.CHART.TIME_LINE_CHART') || 'de serie lineal';
      default:
        return this.translate.instant('DATA_VISUALIZATION.CHART.BAR_CHART') || 'de barras';
    }
  }

  private getChartIcon(type: DataBricksAiGenie_ChartAnalysisResult_ChartType): string {
    switch (type) {
      case 'pie':
        return 'pie_chart';
      case 'time_line':
        return 'timeline';
      default:
        return 'bar_chart';
    }
  }

  private getViewportHeight(): string {
    if (!this.dataSource?.filteredData) return '300px';

    if (this.originalData.length < 2) return '86px';

    if (this.isDialogMode) {
      // For dialog mode, use a more reliable height calculation
      return 'calc(95vh - 120px)';
    }

    // Para modo panel/embebido, calcular altura basada en contenido
    const rowCount = this.originalData.length;
    const headerHeight = 36; // Header row height
    const searchHeight = this.shouldShowSearchInput() ? 56 : 0; // Search field height
    const rowHeight = 32; // Individual row height
    const padding = 8; // Extra padding for safety

    // Calcular altura del viewport (sin incluir footer que está fuera)
    const viewportContentHeight = headerHeight + searchHeight + rowCount * rowHeight + padding;

    // Para modo panel, limitar la altura máxima para evitar overflow
    const maxViewportHeight = 240; // Altura máxima del viewport en panel
    const finalHeight = Math.min(viewportContentHeight, maxViewportHeight);

    return `${finalHeight}px`;
  }

  private shouldShowFooter(): boolean {
    return this.originalData?.length >= 1;
  }

  _cacheShouldShowFooterVisualization = null;
  private shouldShowFooterVisualization(): boolean {
    if (this._cacheShouldShowFooterVisualization !== null) return this._cacheShouldShowFooterVisualization;

    if (this.displayedColumns?.length < 2 || this.dataSource?.filteredData?.length < 2) return false;

    const numericColumns = this.queryProcessResultNumeric;

    if (!(numericColumns?.length > 0)) return false;

    let rv = Boolean(numericColumns.some(col => this.displayedColumns.includes(col)));

    this._cacheShouldShowFooterVisualization = rv;

    return rv;
  }

  resetCacheShouldShowFooterVisualization() {
    this._cacheShouldShowFooterVisualization = null;
  }

  resetAllCaches() {
    this.resetCacheShouldShowFooterVisualization();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();
    if (!this.originalData) return;

    const filteredData = this.originalData.filter(row => {
      return Object.values(row).some(value => value?.toString().toLowerCase().includes(filterValue));
    });

    // Apply current sort if exists
    if (this.sortColumn) {
      this.dataSource.data = filteredData;
      this.sortData(this.sortColumn);
    } else {
      this.dataSource.data = filteredData;
    }
  }

  clearSearch() {
    this.searchValue = '';
    if (this.sortColumn) {
      this.dataSource.data = this.originalData;
      this.sortData(this.sortColumn);
    } else {
      this.dataSource.data = this.originalData;
    }
  }

  shouldShowSearchInput(): boolean {
    return this.originalData?.length > 6;
  }

  shouldShowFullDownloadButton(): boolean {
    const genieConfig = this.dataBricksAiGenieChatService.getGenieConfig();

    // Check if feature is enabled via config flag
    if (genieConfig.shouldShowFullDownloadButton === false) {
      return false;
    }

    const totalRows = this.queryProcessResult?.statement_response?.manifest?.total_row_count;
    const displayedRows = this.dataSource?.filteredData?.length || 0;

    if (!totalRows || !this.hasQuery()) return false;

    const threshold = genieConfig.fullDownloadRowThreshold;

    // Solo mostrar si hay más datos de los que se están mostrando
    // Y además si el total supera el umbral configurado
    return totalRows >= threshold || displayedRows < totalRows;
  }

  sortData(column: string): void {
    // If clicking the same column, toggle direction
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    // Sort the data
    const sortedData = [...this.dataSource.data].sort((a, b) => {
      const valueA = a[column];
      const valueB = b[column];

      // Handle null/undefined values
      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;

      // Handle dates - Check both original date format and our formatted dd/mm/yyyy
      if (this.isFormattedDate(valueA) || isDateString(valueA)) {
        if (this.isFormattedDate(valueB) || isDateString(valueB)) {
          const dateA = this.parsePossibleDate(valueA);
          const dateB = this.parsePossibleDate(valueB);

          if (dateA && dateB) {
            return this.sortDirection === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
          }
        }
      }

      // Handle numbers (including string representations with locale formats)
      const numA = typeof valueA === 'string' ? this.parseLocaleNumber(valueA) : valueA;
      const numB = typeof valueB === 'string' ? this.parseLocaleNumber(valueB) : valueB;

      if (!isNaN(numA) && !isNaN(numB)) {
        return this.sortDirection === 'asc' ? numA - numB : numB - numA;
      }

      // Handle strings and others
      const compareResult = String(valueA).localeCompare(String(valueB));
      return this.sortDirection === 'asc' ? compareResult : -compareResult;
    });

    this.dataSource.data = sortedData;
  }

  private isFormattedDate(value: any): boolean {
    if (typeof value !== 'string') return false;
    return /^\d{2}\/\d{2}\/\d{4}$/.test(value);
  }

  private parsePossibleDate(value: string): Date | null {
    try {
      // First check if it's our formatted date (dd/mm/yyyy)
      if (this.isFormattedDate(value)) {
        const [day, month, year] = value.split('/').map(Number);
        const date = new Date(year, month - 1, day);
        return isNaN(date.getTime()) ? null : date;
      }

      // Then try standard date parsing (for original format)
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }

  private parseLocaleNumber(stringNumber: string): number {
    try {
      // Get the current locale's decimal separator
      const format = new Intl.NumberFormat(this.localStringFormat);
      const parts = format.formatToParts(1.1);
      const decimalSeparator = parts.find(part => part.type === 'decimal')?.value || '.';
      const thousandsSeparator = parts.find(part => part.type === 'group')?.value || ',';

      // Remove thousand separators and normalize decimal separator to '.'
      const normalized = stringNumber.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '').replace(decimalSeparator, '.');

      return parseFloat(normalized);
    } catch (e) {
      return NaN;
    }
  }

  hasQuery(): boolean {
    return !!this.message?.rawResponse?.attachments?.[0]?.query;
  }

  shouldDisplayPinButton(): boolean {
    return this.showPinButton && this.hasQuery() && !!this.aiDashboardStorageService;
  }

  isPinLimitExceeded(): boolean {
    const totalRows = this.queryProcessResult?.statement_response?.manifest?.total_row_count || 0;
    const genieConfig = this.dataBricksAiGenieChatService.getGenieConfig();
    const threshold = genieConfig.maxPinRowThreshold || 1000;

    return totalRows > threshold;
  }

  getQuery(): string {
    return this.message?.rawResponse?.attachments?.[0]?.query?.query || '';
  }

  copyQuery(): void {
    const query = this.getQuery();
    if (!query) return;

    navigator.clipboard.writeText(query).then(
      () => {
        // Opcional: Mostrar un mensaje de éxito usando un snackbar o tooltip
        _log('Query copiado al portapapeles');
      },
      err => {
        console.error('Error al copiar el query:', err);
      }
    );
  }

  /**
   * Convierte `statement_response.result.data_typed_array` (formato tipado de Genie con
   * `{values: [{str, num, bool}, ...]}`) a un array plano `Record<string, any>[]` con
   * `{columnName: value}` por fila. Es el formato que esperan los analyzers de la IA y los
   * transformers del dashboard.
   *
   * Usa la primera muestra (hasta 50 filas) para mantener el prompt acotado — el analyzer
   * solo necesita ver el shape, no toda la data.
   */
  private buildFlatQueryResultForAI(maxRows = 50): Array<Record<string, any>> {
    try {
      const stmt = this.queryProcessResult?.statement_response;
      const cols = stmt?.manifest?.schema?.columns;
      const rows = stmt?.result?.data_typed_array;
      if (!Array.isArray(cols) || !Array.isArray(rows) || rows.length === 0) return [];

      const colNames = cols.map((c: any) => c?.name).filter((n: any): n is string => !!n);
      const sample = rows.slice(0, Math.max(1, maxRows));

      return sample.map((row: any) => {
        const out: Record<string, any> = {};
        const values = row?.values || [];
        colNames.forEach((name, idx) => {
          const v = values[idx];
          if (v == null) {
            out[name] = null;
          } else if (v.num !== undefined) {
            out[name] = Number(v.num);
          } else if (v.bool !== undefined) {
            out[name] = v.bool;
          } else if (v.str !== undefined) {
            // Si el str representa un número, devolverlo como número para que la IA detecte
            // columnas numéricas (ej "55212682"). Si no, dejarlo como string.
            const asNum = Number(v.str);
            out[name] = !isNaN(asNum) && String(asNum) === v.str ? asNum : v.str;
          } else {
            out[name] = null;
          }
        });
        return out;
      });
    } catch (e) {
      _error('[buildFlatQueryResultForAI] threw', e);
      return [];
    }
  }

  openPinDialog(): void {
    const query = this.getQuery();
    if (!query) return;

    // Obtener el prompt del usuario desde el mensaje
    const userPrompt = this.message?.rawResponse?.content || '';

    const dialogRef = this.dialog.open(PinCardDialogComponent, {
      width: '600px',
      data: {
        query: query,
        userPrompt: userPrompt,
      },
    });

    dialogRef.afterClosed().subscribe((result: PinCardDialogResult) => {
      if (result?.confirmed) {
        // Verificar que el servicio esté disponible
        if (!this.aiDashboardStorageService) {
          _error('AiDashboardStorageService no está disponible en este contexto');
          return;
        }

        _log('Generando título y configuración con IA...');

        // Convertir el data_typed_array (formato tipado Genie {values:[{str:...}]}) al formato
        // plano Record<string, any>[] que espera analyzeQueryResultsWithAI. Sin esta conversión
        // la IA recibía datos malformados y caía siempre al fallback heurístico, descartando la
        // sugerencia de seriesColumns / chartType / dataMapping del prompt extendido.
        const flatQueryResult = this.buildFlatQueryResultForAI();

        this.aiDashboardStorageService.analyzeQueryResultsWithAI(flatQueryResult, undefined, userPrompt).subscribe(
          analysis => {
            const generatedTitle = analysis.suggestedTitle || userPrompt.substring(0, 60) || 'Nueva Consulta';
            _log('Título generado por IA:', generatedTitle, 'Análisis:', analysis);

            // Get all existing cards
            this.aiDashboardStorageService.loadCards().subscribe(existingCards => {
              // Ajustar posiciones: mover todas las cards existentes hacia abajo
              const shiftedCards = existingCards.map(card => ({
                ...card,
                y: (card.y || 0) + 1, // Incrementar y en 1 para hacer espacio arriba
              }));

              // Create new card en la posición superior (y: 0).
              // Persistimos el análisis IA para que el dashboard arranque con la config correcta
              // (incluyendo multi-serie cuando aplica) y evite re-llamar a IA en el primer render.
              const availableColumns = flatQueryResult.length > 0 && flatQueryResult[0] ? Object.keys(flatQueryResult[0]) : undefined;

              const newCard: any = {
                id: `custom-query-${Date.now()}`,
                title: generatedTitle,
                query: query,
                aiPrompt: result.userPrompt || generatedTitle,
                type: analysis.type,
                chartType: analysis.chartType,
                dataMapping: analysis.dataMapping,
                description: analysis.reasoning,
                availableColumns,
                x: 0,
                y: 0, // Posición superior
                w: 3,
                h: 1,
              };

              // Add the new card al inicio del array
              const updatedCards = [newCard, ...shiftedCards];
              this.aiDashboardStorageService.saveCards(updatedCards).subscribe();

              _log('Card anclada al dashboard:', newCard);

              // Mostrar notificación de éxito
              const successMessage = this.translate.instant('DATA_VISUALIZATION.PIN.SUCCESS').replace('{title}', generatedTitle);
              const actionText = this.translate.instant('DATA_VISUALIZATION.PIN.ACTION_VIEW');
              this.snackBar
                .open(successMessage, actionText, {
                  duration: _ms('10s'),
                  panelClass: ['success-snackbar'],
                })
                .onAction()
                .subscribe(() => {
                  // Navegar al dashboard cuando se hace clic en "Ver"
                  this.router.navigate(['/ai-dashboard']);
                });

              // Si estamos en la página del dashboard, notificar para refrescar
              const currentUrl = this.router.url;
              if (currentUrl.includes('/ai-dashboard')) {
                _log('📍 Dashboard detectado - notificando para refrescar...');
                this.aiDashboardEventsService.notifyCardAdded();
              }
            });
          },
          error => {
            _error('Error generando título con IA:', error);
            // Fallback: usar el prompt del usuario
            const fallbackTitle = userPrompt.substring(0, 60) || 'Nueva Consulta';

            this.aiDashboardStorageService.loadCards().subscribe(existingCards => {
              const shiftedCards = existingCards.map(card => ({
                ...card,
                y: (card.y || 0) + 1,
              }));

              const newCard = {
                id: `custom-query-${Date.now()}`,
                title: fallbackTitle,
                query: query,
                aiPrompt: result.userPrompt || fallbackTitle,
                x: 0,
                y: 0,
                w: 3,
                h: 1,
              };

              const updatedCards = [newCard, ...shiftedCards];
              this.aiDashboardStorageService.saveCards(updatedCards).subscribe();

              // Mostrar notificación de éxito
              const successMessage = this.translate.instant('DATA_VISUALIZATION.PIN.SUCCESS').replace('{title}', fallbackTitle);
              const actionText = this.translate.instant('DATA_VISUALIZATION.PIN.ACTION_VIEW');
              this.snackBar
                .open(successMessage, actionText, {
                  duration: _ms('10s'),
                  panelClass: ['success-snackbar'],
                })
                .onAction()
                .subscribe(() => {
                  // Navegar al dashboard cuando se hace clic en "Ver"
                  this.router.navigate(['/ai-dashboard']);
                });

              const currentUrl = this.router.url;
              if (currentUrl.includes('/ai-dashboard')) {
                this.aiDashboardEventsService.notifyCardAdded();
              }
            });
          }
        );
      }
    });
  }

  // Translation methods
  getTableTitle(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.TITLE') || 'Tabla de Resultados';
  }

  getSearchPlaceholder(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.SEARCH_PLACEHOLDER') || 'Buscar...';
  }

  getNoDataMessage(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.NO_DATA') || 'No hay datos disponibles.';
  }

  getRecordsText(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.RECORDS') || 'registros';
  }

  getPinTooltip(): string {
    if (this.isPinLimitExceeded()) {
      const genieConfig = this.dataBricksAiGenieChatService.getGenieConfig();
      const threshold = genieConfig.maxPinRowThreshold || 1000;
      return this.translate.instant('DATA_VISUALIZATION.TABLE.PIN_LIMIT_TOOLTIP').replace('{limit}', threshold.toString());
    }
    return this.translate.instant('DATA_VISUALIZATION.TABLE.PIN_TOOLTIP') || 'Anclar al Dashboard';
  }

  getViewQueryTooltip(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.VIEW_QUERY_TOOLTIP') || 'Ver Query';
  }

  getExportCsvTooltip(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.EXPORT_CSV_TOOLTIP') || 'Exportar a CSV';
  }

  getExpandTableTooltip(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.EXPAND_TABLE_TOOLTIP') || 'Ampliar tabla';
  }

  getCopyQueryTooltip(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.COPY_QUERY_TOOLTIP') || 'Copiar Query al Portapapeles';
  }

  getDownloadFullCsvTooltip(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.DOWNLOAD_FULL_CSV_TOOLTIP') || 'Descargar CSV completo';
  }

  getResultsLimitWarningMessage(): string {
    const totalRows = this.queryProcessResult?.statement_response?.manifest?.total_row_count || 0;
    const displayedRows = this.dataSource?.filteredData?.length || 0;

    let message = this.translate.instant('DATA_VISUALIZATION.TABLE.RESULTS_LIMIT_WARNING');

    return message || 'La visualización de la tabla tiene un límite. Para obtener la totalidad de los datos,';
  }

  getClickHereText(): string {
    return this.translate.instant('DATA_VISUALIZATION.TABLE.CLICK_HERE') || 'haz click aquí';
  }

  canUseDevMode(): boolean {
    return this.devSettings.__canUseDevMode();
  }
}
